import React from 'react';
import { Area, AreaChart, ResponsiveContainer, YAxis } from 'recharts';
import { Card, CardContent } from '../ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { MoreHorizontal, TrendingUp, ArrowRight, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';

const COLOR_MAP = {
  default:   { bg: 'bg-gray-50 dark:bg-gray-900/40',    iconBg: 'bg-gray-100 dark:bg-gray-800',    text: 'text-gray-700 dark:text-gray-300',    stroke: 'hsl(210, 15%, 45%)' },
  primary:   { bg: 'bg-blue-50 dark:bg-blue-950/40',    iconBg: 'bg-blue-100 dark:bg-blue-900/50',    text: 'text-blue-700 dark:text-blue-300',    stroke: 'hsl(210, 55%, 40%)' },
  secondary: { bg: 'bg-violet-50 dark:bg-violet-950/40',  iconBg: 'bg-violet-100 dark:bg-violet-900/50',  text: 'text-violet-700 dark:text-violet-300',  stroke: 'hsl(260, 35%, 45%)' },
  success:   { bg: 'bg-green-50 dark:bg-green-950/40',   iconBg: 'bg-green-100 dark:bg-green-900/50',   text: 'text-green-700 dark:text-green-300',   stroke: 'hsl(160, 45%, 35%)' },
  warning:   { bg: 'bg-amber-50 dark:bg-amber-950/40',   iconBg: 'bg-amber-100 dark:bg-amber-900/50',   text: 'text-amber-700 dark:text-amber-300',   stroke: 'hsl(38, 55%, 45%)' },
  danger:    { bg: 'bg-red-50 dark:bg-red-950/40',     iconBg: 'bg-red-100 dark:bg-red-900/50',     text: 'text-red-700 dark:text-red-300',     stroke: 'hsl(4, 65%, 45%)' },
};

const TREND_ICON = {
  success: TrendingUp,
  warning: ArrowRight,
  danger:  TrendingDown,
};

export function ColoredAreaCard({ title, value, change, color = 'default', icon: IconComp, xaxis = 'month', chartData, index = 0, className }) {
  const cfg = COLOR_MAP[color] || COLOR_MAP.default;
  const TrendIcon = TREND_ICON[color] || TrendingDown;
  const gradientId = `coloredArea-${index}`;

  return (
    <Card className={cn('border-0 shadow-none', cfg.bg, className)}>
      <CardContent className="p-0">
        <section className="flex flex-nowrap justify-between relative">
          <div className="flex flex-col justify-between gap-y-2 p-4">
            <div className="flex flex-col gap-y-3">
              <div className="flex items-center gap-x-3">
                {IconComp && (
                  <div className={cn('rounded-md p-2', cfg.iconBg)}>
                    <IconComp className={cn('h-4 w-4', cfg.text)} />
                  </div>
                )}
                <dt className="text-sm font-medium text-[#5B616E] dark:text-white/60">{title}</dt>
              </div>
              <dd className="text-3xl font-semibold text-[#121316] dark:text-white/90">{value}</dd>
            </div>
            <div className={cn('mt-2 flex items-center gap-x-1 text-xs font-medium', cfg.text)}>
              <TrendIcon className="h-4 w-4" />
              <span>{change}</span>
              <span className="text-[#5B616E]">
                {' '}vs {xaxis === 'day' ? 'yesterday' : `last ${xaxis}`}
              </span>
            </div>
          </div>
          <div className="mt-10 min-h-[96px] w-36 min-w-[140px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart accessibilityLayer data={chartData}>
                <defs>
                  <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor={cfg.stroke} stopOpacity={0.2} />
                    <stop offset="10%" stopColor={cfg.stroke} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis domain={[Math.min(...(chartData || []).map(d => d.value)), 'auto']} hide />
                <Area dataKey="value" fill={`url(#${gradientId})`} stroke={cfg.stroke} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 rounded-full">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px]">
              <DropdownMenuItem className="text-xs">View Details</DropdownMenuItem>
              <DropdownMenuItem className="text-xs">Export Data</DropdownMenuItem>
              <DropdownMenuItem className="text-xs">Set Alert</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </section>
      </CardContent>
    </Card>
  );
}

export function ColoredAreaGrid({ items }) {
  return (
    <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item, index) => (
        <ColoredAreaCard key={index} index={index} {...item} />
      ))}
    </div>
  );
}
